import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Chat } from "../../components/chat/Chat";
import { VideoChat } from "../../components/videoChat/VideoChat";
import { VideoPlayer } from "../../components/videoPlayer/VideoPlayer";
import { CommonTip } from "../../components/commonTip/CommonTip";
import { DatePicker } from "../../components/datePicker/DatePicker";
import { Graphic } from "../../components/graphic/Graphic";
import { SpeedChart } from "../../components/chartjs/SpeedChart";
import { VolumeChart } from "../../components/chartjs/VolumeChart";
import { loadFile, uploadJson, loadMultiSessionData, deleteCommonWord } from "../../api/api";
import { useParams, useNavigate } from "react-router-dom";
import { AppContext } from "../../appContext";
import { SubtitlesWidget } from "./components/subtitlesWidget/SubtitlesWidget";
import { MeetingPageNotes } from "./components/meetingPageNotes/MeetingPageNotes";
import { MeetingPageTasks } from "./components/meetingPageTasks/MeetingPageTasks";
import { MeetingPageGoals } from "./components/meetingPageGoals/MeetingPageGoals";
import { MeetingPageNotesWidget } from "./components/meetingPageNotesWidget/MeetingPageNotesWidget";
import { MeetingPageAnalysisWidget } from "./components/meetingPageAnalysisWidget/MeetingPageAnalysisWidget";
import { FirstCommonWord, MostCommonWord } from "./components/mostCommonWord/MostCommonWord";
import { BubblesWidget } from "./components/bubblesWidget/BubblesWidget";
import { WordsTimesDiagram } from "./components/wordsTimesDiagram/WordsTimesDiagram";
import { Collapser } from "../../components/collapser/Collapser";
import { HorizontalCollapser } from "../../components/horizontalCollapser/HorizontalCollapser";
import { MeetingPageHeader } from "./components/meetingPageHeader/MeetingPageHeader";
import { chatMessages, graphicPoints } from "../../mocks/meetingPage";
import { IVideoResponse } from "../../types/videoResponse";
import { loadClients } from "../../api/loadClients";
import { ISession } from "../../types/dashboard";
import { TwoColumnCollapser } from "../../components/twoColumnCollapser/TwoColumnCollapser";
import { SendFolderPopup } from "./components/sendFolderPopup/SendFolderPopup";
import { Spinner } from "../../components/spinner/Spinner";

import "./MeetingPageGraphicSpeed.css";
import "./MeetingPage.css";
import { formatDate2, formatDate3, timestampToDate } from "../../utils/dateFormats";
import { sessions } from "../../mocks/dashboard";
import { IClientData } from "../../types/dashboard";
import { ISelectedRange } from "../../components/subtitles/SubtitlesTypes";

/*interface IClientData {
    id: string;
    name: string;
    avatarUrl: string;
    sessions: ({Result: IVideoResponse} & ISession)[]
}*/

export const MeetingPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    //console.log(params);
    const [columnCollapserState, setColumnCollapserState] = useState('both');
    const sessionId = params.id;
    const clientId = params.client;
    const user = useContext(AppContext).user;
    const debug = useContext(AppContext).cheat.debug;
    const [sessionData, setSessionData] = useState<IVideoResponse>(null);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [selectedTime, setSelectedTime] = useState(0);
    const [selectedTimeSub, setSelectedTimeSub] = useState(0);
    const [isSubtitlesCollapsed, setSubtitlesCollapsed] = useState(true);
    const [isVideoCollapsed, setVideoCollapsed] = useState(false);
    const [isWidgetsCollapsed, setWidgetsCollapsed] = useState(false);
    //const [clients, setClients] = useState<IClientData[]>();
    const {clients, loadSessions, translate} = useContext(AppContext);

    const [currentClient, setCurrentClient] = useState<IClientData>();
    const [currentSession, setCurrentSession] = useState<ISession>();
    const [wordsWidgetData, setWordsWidgetData] = useState<{counts: { value: number, label: string }[], index: number, word: string}>();
    const [bubblesData, setBubblesData] = useState<{ value: number, label: string }[]>();
    const [deleteWordError, setDeleteWordError] = useState<string | null>(null);
    const [videoSubtitlesProportion, setVideoSubtitlesProportion] = useState<number>();
    const videoProportionRef = useRef<HTMLDivElement>(null);
    const [selectedWord, setSelectedWord] = useState('');
    const [sendFolderSelection, setSendFolderSelection] = useState<ISelectedRange>(null);
    const [folderSelectionToAdd, setFolderSelectionToAdd] = useState<ISelectedRange | null>(null);
    const [graphicSpeedPoints, setGraphicSpeedPoints] = useState({values: [], timeMarks: []});
    const [graphicTimesPoints, setGraphicTimesPoints] = useState<{x: number[], y: number[]}>({
        x: [],
        y: []
    });
    const [graphicVolume, setGraphicVolume] = useState({
        volumeY: [],
        volumeTitles: []
    });
    const [sessionsSpeed, setSessionsSpeed] = useState({
        average: 0,
        speeds: [],
        dates: []
    });
    const [mostWord, setMostWord] = useState<{
        mostPopularWord: string,
        mockPopularCount: number,
        newWord: string,
        newWordCount: number
    }>({
        mostPopularWord: null,
        mockPopularCount: null,
        newWord: null,
        newWordCount: null 
    });
    const pointsBub = useMemo(()=>{
        return new Array(18).fill(0).map(it => ({value: Math.floor(Math.random() * Math.random() *  100 + 30), label: ''}));
    }, []);

    // State to hold multi-session data
    const [multiSessionData, setMultiSessionData] = useState<ISession[]>([]);
    const [isLoadingMultiSession, setIsLoadingMultiSession] = useState(false);
    const [multiSessionError, setMultiSessionError] = useState<string | null>(null);
    
    // Ref to track last fetched client ID to prevent duplicate multi-session calls
    const lastFetchedClientIdRef = useRef<string | null>(null);

    useEffect(()=>{
        if (!sessionData || !sessionData.output || !sessionData.output.speedList){
            return;
        }
        const speedEntries = Object.entries(sessionData.output.speedList);
        const points = speedEntries.map(([timeRange, speed]) => speed);
        const timeMarks = speedEntries.map(([timeRange, speed]) => {
            const [start, end] = timeRange.split('-').map(t => parseFloat(t));
            return (start + end) / 2; // Use middle point of the time range
        });
        console.log('Speed points:', points, 'Time marks:', timeMarks);
        setGraphicSpeedPoints({values: points, timeMarks});
    }, [sessionData]);

    useEffect(()=>{
        if (!sessionData || !sessionData.output || !sessionData.output.segments){
            console.log('DEBUG: No session data or segments available');
            return;
        }

        console.log('DEBUG: Complete session data from /result endpoint:', JSON.stringify(sessionData, null, 2));
        console.log('DEBUG: Session data output:', sessionData.output);
        console.log('DEBUG: ClientSpeaker field raw value:', sessionData.output.ClientSpeaker);
        console.log('DEBUG: ClientSpeaker field type:', typeof sessionData.output.ClientSpeaker);

        const segments = sessionData.output.segments;
        if (segments.length === 0) {
            console.log('DEBUG: No segments found in session data');
            return;
        }

        console.log('DEBUG: Number of segments:', segments.length);
        console.log('DEBUG: First 3 segments:', segments.slice(0, 3));

        // Determine which speaker is the client using ClientSpeaker field from API
        const clientSpeaker = sessionData.output.ClientSpeaker;
        let clientSpeakerIdentifier: string;
        
        console.log('DEBUG: Processing ClientSpeaker:', clientSpeaker);
        
        if (!clientSpeaker) {
            console.warn('ClientSpeaker field not found in session data, falling back to duration-based detection');
            // Fallback to original logic if ClientSpeaker field is missing
            const speakerDurations: Record<string, number> = {};
            segments.forEach(segment => {
                const speaker = String(segment.speaker);
                const duration = segment.end - segment.start;
                speakerDurations[speaker] = (speakerDurations[speaker] || 0) + duration;
            });

            console.log('DEBUG: Speaker durations for fallback:', speakerDurations);

            const clientSpeakerEntry = Object.entries(speakerDurations)
                .sort((a, b) => b[1] - a[1])[0];

            if (!clientSpeakerEntry) {
                console.error('DEBUG: No client speaker entry found in fallback');
                return;
            }

            console.log('DEBUG: Client speaker entry from fallback:', clientSpeakerEntry);
            clientSpeakerIdentifier = clientSpeakerEntry[0];
            console.log('DEBUG: Client speaker identifier from fallback:', clientSpeakerIdentifier);
        } else {
            console.log('DEBUG: ClientSpeaker field found:', clientSpeaker);
            clientSpeakerIdentifier = String(clientSpeaker);
            console.log('DEBUG: Client speaker identifier from API:', clientSpeakerIdentifier);
        }

        // Create timeline points
        const points: number[] = [];
        const pointsY: number[] = [];

        // Sort segments by start time
        const sortedSegments = [...segments].sort((a, b) => a.start - b.start);

        // Start from beginning of session
        if (sortedSegments.length > 0 && sortedSegments[0].start > 0) {
            // Silence from start until first segment
            points.push(0);
            pointsY.push(0);
            points.push(sortedSegments[0].start);
            pointsY.push(0);
        }

        for (let i = 0; i < sortedSegments.length; i++) {
            const segment = sortedSegments[i];
            const isClient = String(segment.speaker) === clientSpeakerIdentifier;

            // Add segment start point
            points.push(segment.start);
            pointsY.push(isClient ? 1 : 0);

            // Add segment end point
            points.push(segment.end);
            pointsY.push(isClient ? 1 : 0);

            // Check gap to next segment
            const nextSegment = sortedSegments[i + 1];
            if (nextSegment) {
                const gapDuration = nextSegment.start - segment.end;

                if (gapDuration > 0) {
                    // There's a gap between segments
                    if (isClient && String(nextSegment.speaker) === clientSpeakerIdentifier && gapDuration <= 1.0) {
                        // Client to client with gap <= 1 second, keep as speaking
                        points.push(segment.end);
                        pointsY.push(1);
                        points.push(nextSegment.start);
                        pointsY.push(1);
                    } else {
                        // Gap > 1 second or different speakers, mark as silence
                        points.push(segment.end);
                        pointsY.push(0);
                        points.push(nextSegment.start);
                        pointsY.push(0);
                    }
                }
            }
        }

        console.log('DEBUG: Final taciturnity data - Client speaker:', clientSpeakerIdentifier, 'Total points:', points.length, 'Timeline data generated successfully');
        console.log('DEBUG: Timeline points preview (first 10):', {x: points.slice(0, 10), y: pointsY.slice(0, 10)});

        setGraphicTimesPoints({
            x: points,
            y: pointsY
        });
    }, [sessionData]);

    // Transform raw API data once at the top level
    const processedSessions = useMemo(() => {
        if (!multiSessionData || multiSessionData.length === 0) {
            return [];
        }

        console.log('Processing session data. Raw data:', multiSessionData);

        // Transform the nested session data structure
        const sessions = multiSessionData.map((sessionWrapper: any) => {
            // Get the first (and should be only) key which is the session ID
            const sessionId = Object.keys(sessionWrapper)[0];
            const sessionData = sessionWrapper[sessionId];

            if (!sessionData) {
                console.warn('No session data found in wrapper:', sessionWrapper);
                return null;
            }

            // Return the session with proper structure
            return {
                ...sessionData,
                id: sessionId,
                // Map API field names to expected field names
                most_common_words: sessionData.MostCommonWords, // API uses capital M
                AvarageSpeed: sessionData.AvarageSpeed,
                Volume: sessionData.Volume,
                date: sessionData.date,
                dateTo: sessionData.dateTo,
                Error: sessionData.Error
            };
        }).filter(session => session !== null); // Remove any null entries

        console.log('Processed sessions:', sessions.length, sessions);
        return sessions;
    }, [multiSessionData]);

    // Static bubbles data - calculated once and doesn't change when word is selected
    const staticBubblesData = useMemo(() => {
        if (!processedSessions || processedSessions.length === 0 || !sessionId) {
            return null;
        }

        const wordsSessions = [...processedSessions].filter(it => {
            const hasWords = (it.most_common_words && Array.isArray(it.most_common_words)) || 
                             (it.MostCommonWords && Array.isArray(it.MostCommonWords));
            return !it.Error && hasWords;
        }).sort((a, b) => Number(a.date > b.date) * 2 - 1);

        const currentSessionData = wordsSessions.find(it => String(it.id) === String(sessionId));

        if (currentSessionData) {
            const wordsData = currentSessionData.most_common_words || currentSessionData.MostCommonWords;
            if (wordsData && Array.isArray(wordsData)) {
                return wordsData.slice(0, 10).map((it: {word: string, count: number}) => ({
                    label: it.word, 
                    value: it.count
                }));
            }
        }

        return null;
    }, [processedSessions, sessionId]);

    // Consolidated widget data processing (excluding bubbles which are handled separately)
    const widgetData = useMemo(() => {
        if (!processedSessions || processedSessions.length === 0 || !sessionId) {
            console.log('No processed sessions for widget data');
            return {
                volumeData: { volumeY: [], volumeTitles: [] },
                speedData: { average: 0, speeds: [], dates: [] },
                mostWordData: {
                    mostPopularWord: null,
                    mockPopularCount: null,
                    newWord: null,
                    newWordCount: null 
                },
                wordsWidgetData: null
            };
        }

        console.log('Processing all widget data from processed sessions:', processedSessions);

        // Volume data processing
        const volumeSessions = [...processedSessions].filter(it => {
            return !it.Error && it.Volume !== undefined && it.Volume !== null;
        }).sort((a, b) => Number(a.date > b.date) * 2 - 1);

        const volumeY = volumeSessions.map(session => session.Volume);
        const volumeTitles = volumeSessions.map(session => {
            const rawDate = session.date;
            const timestampDate = timestampToDate(rawDate);
            const formattedDate = formatDate3(timestampDate);
            return formattedDate;
        });

        // Speed data processing
        const speedSessions = [...processedSessions].filter(it => {
            return !it.Error && it.AvarageSpeed !== undefined && it.AvarageSpeed !== null;
        }).sort((a, b) => Number(a.date > b.date) * 2 - 1);

        const speeds = speedSessions.map(it => it.AvarageSpeed);
        const dates = speedSessions.map(it => {
            const date = it.date;
            return typeof date === 'string' ? parseFloat(date) : date;
        });
        const average = speeds.length > 0 ? speeds.reduce((ac, it) => ac + it, 0) / speeds.length : 0;

        // Words processing for most common words analysis and words widget
        const wordsSessions = [...processedSessions].filter(it => {
            const hasWords = (it.most_common_words && Array.isArray(it.most_common_words)) || 
                             (it.MostCommonWords && Array.isArray(it.MostCommonWords));
            return !it.Error && hasWords;
        }).sort((a, b) => Number(a.date > b.date) * 2 - 1);

        const currentSessionData = wordsSessions.find(it => String(it.id) === String(sessionId));
        let mostWordData = {
            mostPopularWord: null as string | null,
            mockPopularCount: null as number | null,
            newWord: null as string | null,
            newWordCount: null as number | null 
        };
        let wordsWidgetData = null;

        if (currentSessionData) {
            const wordsData = currentSessionData.most_common_words || currentSessionData.MostCommonWords;

            // Words widget data for WordsTimesDiagram
            if (wordsData && Array.isArray(wordsData) && wordsData.length > 0) {
                // Use selectedWord if available, otherwise use the most common word from current session
                let targetWord = selectedWord;
                if (!targetWord) {
                    const sortedWords = [...wordsData].sort((a, b) => b.count - a.count);
                    const topWord = sortedWords[0];
                    targetWord = topWord.word;
                }

                // Create timeline data showing this word's usage across sessions
                const counts = wordsSessions.map((session, index) => {
                    const sessionWords = session.most_common_words || session.MostCommonWords;
                    const wordUsage = sessionWords?.find((w: {word: string, count: number}) => w.word === targetWord);
                    return {
                        value: wordUsage ? wordUsage.count : 0,
                        label: `Session ${index + 1}`
                    };
                });

                const currentIndex = wordsSessions.findIndex(it => String(it.id) === String(sessionId));

                wordsWidgetData = {
                    counts: counts,
                    index: currentIndex >= 0 ? currentIndex : 0,
                    word: targetWord
                };
            }

            // Most common words analysis
            const allWords: Record<string, {word: string, count: number }> = {};
            const allWordsNotCurrent: Record<string, {word: string, count: number }> = {};

            wordsSessions.forEach(it => {
                const sessionWordsData = it.most_common_words || it.MostCommonWords;
                if (sessionWordsData && Array.isArray(sessionWordsData)) {
                    sessionWordsData.forEach((wordData: {word: string, count: number}) => {
                        if (!wordData || typeof wordData.word !== 'string' || typeof wordData.count !== 'number') {
                            return;
                        }
                        const word = wordData.word;
                        const count = wordData.count;

                        if (allWords[word]) {
                            allWords[word].count += count;
                        } else {
                            allWords[word] = {word: word, count: count};
                        }

                        if (String(it.id) !== String(currentSessionData.id)) {
                            if (allWordsNotCurrent[word]) {
                                allWordsNotCurrent[word].count += count;
                            } else {
                                allWordsNotCurrent[word] = {word: word, count: count};
                            }
                        }
                    });
                }
            });

            const sorted = Object.values(allWords).sort((a, b) => b.count - a.count);
            const mostPopular = sorted[0];

            const currentWordsData = currentSessionData.most_common_words || currentSessionData.MostCommonWords;
            const newWords = (currentWordsData && Array.isArray(currentWordsData)) 
                ? currentWordsData.filter((wordData: {word: string, count: number}) => {
                    if (!wordData || typeof wordData.word !== 'string') {
                        return false;
                    }
                    const word = wordData.word;
                    return !(allWordsNotCurrent[word]);
                }) 
                : [];

            const sortedNews = newWords.sort((a: {word: string, count: number}, b: {word: string, count: number}) => b.count - a.count);

            mostWordData = {
                mostPopularWord: mostPopular?.word || null,
                mockPopularCount: mostPopular?.count || null,
                newWord: sortedNews[0]?.word || null,
                newWordCount: sortedNews[0]?.count || null
            };
        }

        return {
            volumeData: { volumeY, volumeTitles },
            speedData: { average, speeds, dates },
            mostWordData,
            wordsWidgetData
        };
    }, [processedSessions, sessionId, selectedWord]);

    // Update individual state values only when consolidated data changes
    // Optimize widget state updates - batch them into a single state update to reduce re-renders
    useEffect(() => {
        if (widgetData) {
            // Batch all widget state updates together to minimize re-renders
            setGraphicVolume(widgetData.volumeData);
            setSessionsSpeed(widgetData.speedData);
            setMostWord(widgetData.mostWordData);
            setWordsWidgetData(widgetData.wordsWidgetData);
        }
    }, [widgetData]);

    // Update bubbles data separately - only when static data changes, not when word is selected
    // Prevent unnecessary transitions by only updating when we have real data
    useEffect(() => {
        if (staticBubblesData && Array.isArray(staticBubblesData) && staticBubblesData !== bubblesData) {
            setBubblesData(staticBubblesData);
        }
    }, [staticBubblesData]);

    

    useEffect(()=>{
        if (!currentClient){
            return;
        }
        // Only set current session - loadSessions is handled in the clients-based effect
        if (currentClient.sessions) {
            const currentSession = currentClient.sessions.find((jt: any)=>jt.id == sessionId);
            setCurrentSession(currentSession);
        }
    }, [currentClient, currentClient?.sessions]);

    useEffect(() => {
        const title = currentSession?.title;
        document.title = title ? `Kitsugi · ${title}` : 'Kitsugi · Session';
        return () => {
            document.title = 'Kitsugi · Session';
        };
    }, [currentSession]);

    

    //useEffect(()=>{
    //videoResize
    //    if (videoProportionRef.current){
    //        const currentHeight = videoProportionRef.current.getBoundingClientRect().height;
    //        if (videoSubtitlesProportion == undefined){
    //            //setVideoSubtitlesProportion(currentHeight);
    //        } else if (videoSubtitlesProportion > currentHeight){
    //            setVideoSubtitlesProportion(videoProportionRef.current.getBoundingClientRect().height);
    //        }
    //        /*if (videoSubtitlesProportion < 100){
    //            setVideoSubtitlesProportion(100);
    //        }*/
    //    }
    //}, [videoProportionRef.current, videoSubtitlesProportion]);

    useEffect(()=>{
        if (!user || !sessionId){
            console.log('not auth or no sessionId');
            return;
        }

        setIsLoadingSession(true);
        loadFile(sessionId)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Failed to load session: ${res.status}`);
                }
                return res.json();
            })
            .then(data => {
                console.log('Session data loaded:', data);
                setSessionData(data);
            })
            .catch(error => {
                console.error('Error loading session data:', error);
                // Still set empty data to prevent infinite loading
                setSessionData({ output: { segments: [], speedList: {} } } as any);
            })
            .finally(() => {
                setIsLoadingSession(false);
            });
    }, [sessionId, user]);

    useEffect(()=>{
        if (!clients || !sessionId || !clientId){
            return;
        }

        const client = clients.find(it => it.id == clientId);
        setCurrentClient(client);

        // Preload sessions if not loaded
        if (client && !client.sessions) {
            loadSessions(client.id);
        }

        // Load multi-session data for widgets - only if not already loading and client changed
        if (client && !isLoadingMultiSession && lastFetchedClientIdRef.current !== clientId) {
            lastFetchedClientIdRef.current = clientId;
            setIsLoadingMultiSession(true);
            setMultiSessionError(null);

            loadMultiSessionData(clientId)
                .then(res => {
                    if (!res.ok) {
                        throw new Error(`Failed to load multi-session data: ${res.status}`);
                    }
                    return res.json();
                })
                .then((sessions: ISession[]) => {
                    console.log('Multi-session data loaded:', sessions);
                    setMultiSessionData(sessions);
                    // Keep the ref set to current client after successful load
                })
                .catch(error => {
                    console.error('Error loading multi-session data:', error);
                    setMultiSessionError(error.message);
                    // Set empty array as fallback
                    setMultiSessionData([]);
                    // Reset ref on error so retry is possible
                    lastFetchedClientIdRef.current = null;
                })
                .finally(() => {
                    setIsLoadingMultiSession(false);
                });
        }
    }, [clients, sessionId, clientId, isLoadingMultiSession]);

    

    

    useEffect(()=>{console.log('bubbles', bubblesData)}, [bubblesData])

    const points = useMemo(()=>{
        return new Array(8).fill(null).map((it, i)=>{
            return {
                value: Math.floor(Math.random() * 30 + 1),
                label: `${i}:${i}`
            }
        })
    }, []);

    const activeTimesPoint = 4;
    //const mockPopularWord = 'Father'; // This seems to be a mock value, not used directly in rendering.

    const isLiveSession = false; // Assuming this is a static value for now.

    // Handle delete word functionality
    const handleDeleteWord = async (word: string) => {
        if (!sessionId || !bubblesData) return;
        
        // Find the word index in the current bubbles data
        const wordIndex = bubblesData.findIndex(item => item.label === word);
        if (wordIndex === -1) return;
        
        try {
            setDeleteWordError(null);
            const response = await deleteCommonWord(encodeURIComponent(sessionId), wordIndex);
            
            // Handle case where sendRequest returns null (e.g., account disabled)
            if (!response) {
                setDeleteWordError(translate('meeting_auth_error'));
                return;
            }
            
            if (response.ok) {
                try {
                    const result = await response.json();
                    // Update bubblesData with the new MostCommonWords from the response
                    if (result.MostCommonWords && Array.isArray(result.MostCommonWords)) {
                        const newBubblesData = result.MostCommonWords.slice(0, 10).map((item: {word: string, count: number}) => ({
                            label: item.word,
                            value: item.count
                        }));
                        setBubblesData(newBubblesData);
                    } else {
                        setDeleteWordError(translate('meeting_invalid_response'));
                    }
                } catch (parseError) {
                    console.error('Error parsing success response:', parseError);
                    setDeleteWordError(translate('meeting_processing_error'));
                }
            } else {
                // Handle error responses safely
                let errorMessage = `Failed to delete word (${response.status})`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (parseError) {
                    // If JSON parsing fails, try to get text response or use status
                    try {
                        const textResponse = await response.text();
                        if (textResponse) {
                            errorMessage = textResponse;
                        }
                    } catch (textError) {
                        // Use default error message with status code
                    }
                }
                setDeleteWordError(errorMessage);
            }
        } catch (error) {
            console.error('Error deleting word:', error);
            setDeleteWordError(translate('meeting_network_error'));
        }
    };

    const widgetsColumn = (
    <div className={`scroll meetingPage_widgets ${isVideoCollapsed ? 'meetingPage_widgets--collapsed' : ''}`} onClick={()=>setWidgetsCollapsed(true)}>
        <MeetingPageTasks 
            selectedClientId={currentClient?.id}
            selectedClientName={currentClient?.name}
            sessionId={sessionId}
            sessionDate={currentSession?.startDatetime || currentSession?.date}
        />
        <MeetingPageGoals 
            selectedClientId={currentClient?.id}
            selectedClientName={currentClient?.name}
        />
        <MeetingPageNotesWidget 
            selectedClientId={currentClient?.id}
            selectedClientName={currentClient?.name}
        />
        <MeetingPageAnalysisWidget 
            selectedClientId={currentClient?.id}
            selectedClientName={currentClient?.name}
        />
        <div className="meetingPage_chat">
            {debug && <div className="meetingPage_bottom_graphic">
                <Chat messages={chatMessages}></Chat>
            </div>
            }

            {/* Only render widgets after data is loaded to improve initial render speed */}
            {sessionData && (
                <div className="meetingPage_bottom_graphic">
                    <div className="meetingPage_graphicSpeed">
                        {isLoadingMultiSession ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                <Spinner />
                                <span style={{ marginLeft: '10px' }}>{translate('loading')}...</span>
                            </div>
                        ) : multiSessionError ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#f44336' }}>
                                Error loading widget data: {multiSessionError}
                            </div>
                        ) : deleteWordError ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#f44336' }}>
                                {deleteWordError}
                                <div style={{ marginTop: '10px' }}>
                                    <button onClick={() => setDeleteWordError(null)} style={{ padding: '5px 10px', cursor: 'pointer' }}>
                                        {translate('close')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <BubblesWidget 
                                points={bubblesData || pointsBub} 
                                date={currentSession?.date && new Date(currentSession?.date)} 
                                onSelect={(word)=>{
                                    setSelectedWord(word);
                                }}
                                onDelete={handleDeleteWord}
                                selectedWord={selectedWord}
                                wordsWidgetData={wordsWidgetData}
                                onBack={() => setSelectedWord('')}
                            ></BubblesWidget>
                        )}
                    </div>
                </div>
            )}

            {debug && <div className="meetingPage_bottom_graphic"><CommonTip></CommonTip></div>}
            {debug && <div className="meetingPage_bottom_graphic"><DatePicker></DatePicker></div>}
            <div className="meetingPage_bottom_graphic">
                <MostCommonWord word={mostWord.mostPopularWord || ' - '} count={mostWord.mockPopularCount || 0}></MostCommonWord>
            </div>
            <div className="meetingPage_bottom_graphic">
                <FirstCommonWord word={mostWord.newWord || ' - '} name={currentClient?.name || translate('meeting_default_name')}></FirstCommonWord>
            </div>

            <div className="meetingPage_bottom_graphic">
                <SpeedChart
                    values={graphicSpeedPoints.values}
                    timeMarks={graphicSpeedPoints.timeMarks}
                    average={sessionsSpeed?.average}
                    title={translate('meetingPage_todays_speed')}
                    subtitle={`${formatDate3(new Date(currentSession?.date))} ${translate('meetingPage_session_statistics')}`}
                    bottomTextA={translate('meetingPage_todays_average_speed')}
                    bottomTextB={translate('meetingPage_words_per_minute')}
                />
            </div>
            <div className="meetingPage_bottom_graphic">
                <SpeedChart
                    values={sessionsSpeed.speeds}
                    timeMarks={sessionsSpeed.speeds.map((_, index) => index + 1)}
                    average={sessionsSpeed.average}
                    title={translate('meetingPage_average_speed_title')}
                    subtitle={translate('meetingPage_average_speed_subtitle')}
                    bottomTextA={translate('meetingPage_graphicSpeed_bottom_a')}
                    bottomTextB={translate('meetingPage_average_speed_bottom')}
                />
            </div>
            <div className="meetingPage_bottom_graphic">
                <VolumeChart
                    values={graphicTimesPoints.y}
                    labels={graphicTimesPoints.x.map((time, index) => {
                        const minutes = Math.floor(time / 60);
                        const seconds = Math.floor(time % 60);
                        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                    })}
                    title={translate('meetingPage_taciturnity_title')}
                    subtitle={translate('meetingPage_taciturnity_text')
                        .replace('[name]', currentClient?.name || 'Client')
                        .replace('[percent]', Math.floor(sessionData?.silence_percentage || 0).toString())}
                    bottomTextA=""
                    bottomTextB=""
                    isBinaryMode={true}
                    timeValues={graphicTimesPoints.x}
                />
            </div>
            <div className="meetingPage_bottom_graphic">
                <VolumeChart
                    values={graphicVolume.volumeY}
                    labels={graphicVolume.volumeTitles}
                    title={translate('meetingPage_graphicSpeed_title')}
                    subtitle={translate('meetingPage_graphicSpeed_text')
                        .replace('[name]', currentClient?.name || 'Client')}
                    bottomTextA={translate('meetingPage_volume_average')}
                    bottomTextB=""
                    currentSessionId={sessionId}
                    sessions={processedSessions?.filter(s => !s.Error && s.Volume != null) || []}
                />
            </div>
        </div>
    </div>
    )

    const videoColumn = (
        <div className="meetingPage_video" >
            {/*<div className="button meetingPage_video_collapse_button" onClick={()=>setVideoCollapsed(true)}>cl</div>*/}
            <div ref={videoProportionRef} style={{height: (!isSubtitlesCollapsed || videoSubtitlesProportion == undefined) ? '': videoSubtitlesProportion+'px'}}>
                <Collapser collapsed={isSubtitlesCollapsed} style={{height: (!isSubtitlesCollapsed || videoSubtitlesProportion == undefined) ? '' : '100%'}}>
                    {
                        isLiveSession ? (
                        <VideoChat videoUrl={sessionData?.media_file} 
                            selectedTime={selectedTime} 
                        >
                        </VideoChat> ) : (
                        <VideoPlayer videoUrl={sessionData?.media_file} 
                            selectedTime={selectedTime} 
                            onTimeUpdate={(time)=>{
                                setSelectedTimeSub(time)
                            }}
                        >
                        </VideoPlayer> )
                    }
                </Collapser>
            </div>
            <div className="meetingPage_video_subtitles_splitter" onMouseDown={(downEvent)=>{
                //video resize
                if (videoSubtitlesProportion == undefined){
                    const currentHeight = videoProportionRef.current.getBoundingClientRect().height;
                    setVideoSubtitlesProportion(currentHeight);
                }
                const moveHandler = (e: MouseEvent)=>{
                    setVideoSubtitlesProportion(last => Math.max(last+e.movementY, 65));
                }
                const upHandler = (e: MouseEvent)=>{
                    window.removeEventListener('mousemove', moveHandler);
                    window.removeEventListener('mouseup', upHandler);
                }
                window.addEventListener('mousemove', moveHandler);
                window.addEventListener('mouseup', upHandler);
            }}>splitter</div>
            {sessionData?.output && (
                <SubtitlesWidget 
                    date={currentSession?.date}
                    startDatetime={currentSession?.startDatetime}
                    subtitles={sessionData?.output} 
                    onSelectTime={(time)=>setSelectedTime(time)}
                    selectedTime = {selectedTimeSub}
                    isCollapsed={isSubtitlesCollapsed} 
                    onCollapse={()=>setSubtitlesCollapsed((last)=>!last)}
                    folderSelectionToAdd={folderSelectionToAdd}
                    onSelectionAction={(action, selection)=>{
                        if (action == 'sendFolder'){
                            setSendFolderSelection(selection);
                        }
                    }}
                    onSave={(selections)=>{
                        return uploadJson({
                            session_id: sessionId,
                            output: {
                                ...sessionData.output,
                                selections: selections
                            }
                        })
                    }}
                    onWordSave={(segmentIndex, wordIndex, text)=> {setSessionData((last:IVideoResponse)=>{
                        last.output.segments[segmentIndex].words[wordIndex].word = text;
                        return {...last, output: {...last.output}};
                    })}}
                ></SubtitlesWidget>
            )}
        </div>
    )

    // Show loading spinner if data is still loading
    if (isLoadingSession || !sessionData) {
        return (
            <div className="meetingPage">
                <MeetingPageHeader 
                    clientName={currentClient?.name || translate('loading')} 
                    session={currentSession}
                    clientAvatar={currentClient?.avatarUrl}
                    onViewProfile={() => {}}
                />
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div className="meetingPage">
            <MeetingPageHeader 
                clientName={currentClient?.name || ''} 
                session={currentSession}
                clientAvatar={currentClient?.avatarUrl}
                onViewProfile={() => {
                    // Navigate to dashboard with edit client popup
                    navigate(`/dashboard/${currentClient?.id}`);
                    // Use setTimeout to ensure navigation happens first
                    setTimeout(() => {
                        // Trigger edit client popup through URL params or global state
                        const dashboardContext = document.querySelector('[data-dashboard-context]');
                        if (dashboardContext) {
                            // Set up the edit client popup state
                            window.dispatchEvent(new CustomEvent('openEditClient', { detail: currentClient?.id }));
                        }
                    }, 100);
                }}
            ></MeetingPageHeader>
            <div className="meetingPage_center">
                {/*<div className={`meetingPage_video_collapser ${isVideoCollapsed ? 'meetingPage_video_collapser--collapsed' : ''}`}>*/}

                {/*<div className="meetingPage_video_collapser_hold_zero">
                    <div className="meetingPage_video_collapser_hold">
                        <div className="button meetingPage_video_collapse_button meetingPage_video_collapse_button_right" onClick={()=>setVideoCollapsed(false)}>cl</div>
                    </div>
                </div>*/}
                {/*</div>*/}
                {/*<HorizontalCollapser collapsed={isWidgetsCollapsed} 
                    style = {{"--contentSize": "320px"}}
                    content={
                        <div className={`scroll meetingPage_widgets ${isVideoCollapsed ? 'meetingPage_widgets--collapsed' : ''}`} onClick={()=>setWidgetsCollapsed(true)}>
                            <div className="meetingPage_chat">
                                <Chat messages={chatMessages}></Chat>
                                <WordsTimesDiagram points={points} activeTimesPoint={activeTimesPoint}></WordsTimesDiagram>
                                <BubblesWidget points={pointsBub}></BubblesWidget>
                                <CommonTip></CommonTip>
                                <DatePicker></DatePicker>
                            </div>
                        </div> 
                    }
                    holder={
                        <div onClick={()=>setWidgetsCollapsed(false)}>Cl</div>
                    }
                ></HorizontalCollapser>*/}
                <div className="meetingPage_TwoColumnButtons">
                    <div className={`button meetingPage_TwoColumnButton meetingPage_TwoColumnButton_left ${columnCollapserState == 'left' ? 'meetingPage_TwoColumnButton--active' : 0}`} onClick={()=>setColumnCollapserState('left')}>left</div>
                    <div className={`button meetingPage_TwoColumnButton meetingPage_TwoColumnButton_both ${columnCollapserState == 'both' ? 'meetingPage_TwoColumnButton--active' : 0}`} onClick={()=>setColumnCollapserState('both')}>both</div>
                    <div className={`button meetingPage_TwoColumnButton meetingPage_TwoColumnButton_right ${columnCollapserState == 'right' ? 'meetingPage_TwoColumnButton--active' : 0}`} onClick={()=>setColumnCollapserState('right')}>right</div>
                </div>
                <TwoColumnCollapser
                    leftContent = {videoColumn}
                    rightContent = {widgetsColumn}
                    collapserState= {columnCollapserState as any}
                    styles={{
                        rightSide: 'meetingPage_TwoColumnCollapser_right',
                        wrapper: 'meetingPage_TwoColumnCollapser_wrapper'
                    }}
                ></TwoColumnCollapser>
            </div>
            {isLiveSession && <div className="meetingPage_bottom">
                <MeetingPageNotes></MeetingPageNotes>
                <div className="meetingPage_bottom_graphic">
                    <Graphic values={graphicPoints}></Graphic>
                </div>
            </div>}
            {   
                sendFolderSelection && <SendFolderPopup
                    commonSession={currentSession}
                    client={currentClient}
                    sessionData={sessionData}
                    selection={sendFolderSelection}
                    onClose={()=>{setSendFolderSelection(null)}}
                    onSubmit={(folderName)=>{
                        setFolderSelectionToAdd({
                            ...sendFolderSelection,
                            type: 'folder',
                            folderName: folderName || translate('folder')
                        });
                        setSendFolderSelection(null);
                    }}
                ></SendFolderPopup>
            }
        </div>
    )
}